import { Option, Result, Schema } from "effect";
import { ValidatedPlatformIdentity, type BootstrapCapability } from "../identity/service.ts";
import { AuthorizationError, StableIdentityText, OpaqueSessionToken, STATION_SESSION_COOKIE, STATION_CSRF_HEADER,
  type DeploymentBootstrapPolicy, type AuthenticatedStationPrincipal, type StationCapability } from "../model.ts";
import { AuthorizationServiceInvariantError, authorizationOperationError } from "./io.ts";
import { decideStationCapability } from "../decisions.ts";

const identity = Schema.decodeUnknownResult(ValidatedPlatformIdentity);
const token = Schema.decodeUnknownOption(OpaqueSessionToken);
const cookies = Schema.decodeUnknownOption(Schema.Tuple([OpaqueSessionToken]));
const bearer = Schema.decodeUnknownOption(Schema.String.check(Schema.isPattern(/^Bearer [A-Za-z0-9_-]{43}$/i)));
const origin = Schema.decodeUnknownOption(Schema.URLFromString);
export const inspectAuthorization = <A>(body: () => A) => Result.try({ try: body, catch: authorizationOperationError });
export const stableAuthorizationText = (value: unknown, maximum = 256) => Option.getOrNull(Schema.decodeUnknownOption(StableIdentityText(maximum))(value));
export const isSessionToken = Schema.is(OpaqueSessionToken);
export const requireStationCapability = (principal: AuthenticatedStationPrincipal | null, stationId: string, capability: StationCapability) =>
  inspectAuthorization(() => {
    const decision = decideStationCapability(principal, stationId, capability);
    return decision.allowed ? Result.succeed(undefined)
      : Result.fail(new AuthorizationError("authorization-denied", `Station capability denied: ${decision.reason}.`));
  }).pipe(Result.flatMap(result => result));

/** One synchronous authority check: getters cannot yield between approval and capability construction. */
export const authorizeBootstrap = (input: ValidatedPlatformIdentity, policy: DeploymentBootstrapPolicy) => inspectAuthorization(() => {
  if (Result.isFailure(identity(input))) return Result.fail(new AuthorizationError("authentication-required", "A validated platform identity is required."));
  const policySource = stableAuthorizationText(policy.policySource, 512);
  if (!policySource) return Result.fail(new AuthorizationError("bootstrap-denied", "Bootstrap policy is invalid."));
  const subjects = new Set(policy.allowedSubjects ?? []), groups = new Set(policy.allowedGroups ?? []);
  if ((!subjects.size && !groups.size) || (!subjects.has(input.subject) && !input.groups.some(group => groups.has(group)))) {
    return Result.fail(new AuthorizationError("bootstrap-denied", "The platform principal is not permitted by bootstrap policy."));
  }
  return Result.succeed(Object.freeze({ kind: "station-bootstrap" as const, identity: input, policySource }) satisfies BootstrapCapability);
}).pipe(Result.flatMap(result => result));

export const cookieToken = (request: Request) => inspectAuthorization(() => {
  const cookie = request.headers.get("cookie");
  if (!cookie) return null;
  const values = cookie.split(";").map(part => part.trim()).filter(part => part.startsWith(STATION_SESSION_COOKIE + "="))
    .map(part => part.slice(STATION_SESSION_COOKIE.length + 1));
  return Option.getOrNull(Option.map(cookies(values), ([value]) => value));
});
export const bearerToken = (request: Request) => inspectAuthorization(() => Option.getOrNull(Option.map(
  bearer(request.headers.get("authorization")), value => value.slice(7)
)));
export const mutationOrigin = (request: Request, origins: readonly string[]) => inspectAuthorization(() => {
  if (["GET", "HEAD", "OPTIONS"].includes(request.method.toUpperCase())) return Result.succeed(false);
  const value = request.headers.get("origin");
  if (!value || value === "null" || !origins.some(allowed => Option.exists(origin(allowed), parsed => parsed.origin === allowed && allowed === value))) {
    return Result.fail(new AuthorizationError("invalid-origin", "The mutation Origin is not allowed."));
  }
  return Result.succeed(true);
}).pipe(Result.flatMap(result => result));
export const csrfToken = (request: Request) => inspectAuthorization(() => request.headers.get(STATION_CSRF_HEADER));

export const browserSessionCookie = (value: string, maxAgeSeconds: number) => inspectAuthorization(() => {
  const parsed = token(value);
  if (Option.isNone(parsed)) return Result.fail(new AuthorizationServiceInvariantError({ reason: "session-token" }));
  const maxAge = Math.max(0, Math.floor(maxAgeSeconds));
  return Result.succeed(`${STATION_SESSION_COOKIE}=${parsed.value}; Path=/; Max-Age=${maxAge}; Secure; HttpOnly; SameSite=Strict`);
}).pipe(Result.flatMap(result => result));

export const constantTimeEqual = (left: string, right: string) => {
  let different = left.length ^ right.length;
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index++) different |= (left.charCodeAt(index % left.length) || 0) ^ (right.charCodeAt(index % right.length) || 0);
  return different === 0;
};
